'use client';

import { useEffect, useRef, useState } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { A2_COLOR, NON_COLOR, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

type View = 'function' | 'derivative';

/** Negative slope used in A2's leaky ReLU. */
const ALPHA = 0.01;

const X_MIN = -3;
const X_MAX = 3;
const STEP = 0.05;

function buildXs() {
  const xs: number[] = [];
  for (let x = X_MIN; x <= X_MAX + 1e-9; x += STEP) {
    xs.push(Math.round(x * 100) / 100);
  }
  return xs;
}

const XS = buildXs();

const tanhPts = () => XS.map((x) => ({ x, y: Math.tanh(x) }));
const leakyPts = () => XS.map((x) => ({ x, y: x > 0 ? x : ALPHA * x }));
const tanhPrimePts = () => XS.map((x) => ({ x, y: 1 - Math.tanh(x) ** 2 }));
const leakyPrimePts = () => [
  { x: X_MIN, y: ALPHA },
  { x: -0.001, y: ALPHA },
  { x: 0.001, y: 1 },
  { x: X_MAX, y: 1 },
];

const SUBTITLES: Record<View, string> = {
  function: `Output vs input, leaky ReLU negative slope α = ${ALPHA}`,
  derivative: 'Slope vs input, Tanh flattens toward zero while leaky ReLU holds steady',
};

export default function A2Activations() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [view, setView] = useState<View>('function');

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      if (!canvasRef.current) return;

      const isFn = view === 'function';
      const tanhData = isFn ? tanhPts() : tanhPrimePts();
      const leakyData = isFn ? leakyPts() : leakyPrimePts();

      const crosshairPlugin: Plugin<'line'> = {
        id: 'activationCrosshair',
        afterDatasetsDraw(c) {
          const actives = c.tooltip?.getActiveElements();
          if (!actives || actives.length === 0) return;
          const x = actives[0].element.x;
          const { top, bottom } = c.chartArea;
          const ctx = c.ctx;
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.2)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, top);
          ctx.lineTo(x, bottom);
          ctx.stroke();
          ctx.restore();
        },
      };

      const endLabelPlugin: Plugin<'line'> = {
        id: 'activationEndLabels',
        afterDatasetsDraw(c) {
          const ctx = c.ctx;
          ctx.save();
          ctx.font = 'bold 13px Arial, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          c.data.datasets.forEach((ds, i) => {
            const meta = c.getDatasetMeta(i);
            const last = meta.data[meta.data.length - 1];
            if (!last) return;
            ctx.fillStyle = typeof ds.borderColor === 'string' ? ds.borderColor : '#fff';
            ctx.fillText(ds.label ?? '', last.x + 8, last.y);
          });
          ctx.restore();
        },
      };

      const zeroLine = (value: number) =>
        value === 0 ? 'rgba(255,255,255,0.35)' : 'rgba(128,128,128,0.15)';

      const axisTicks = {
        font: { family: 'Arial', size: 13 },
        color: 'rgba(255,255,255,0.85)',
      };
      const axisTitle = (text: string) => ({
        display: true,
        text,
        font: { family: 'Arial', size: 14 },
        color: 'rgba(255,255,255,0.9)',
      });

      chart = new Chart(canvasRef.current, {
        type: 'line',
        data: {
          datasets: [
            {
              label: 'Tanh (A1)',
              data: tanhData,
              borderColor: NON_COLOR,
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: NON_COLOR,
              tension: 0,
            },
            {
              label: 'Leaky ReLU (A2)',
              data: leakyData,
              borderColor: A2_COLOR,
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: A2_COLOR,
              tension: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800, easing: 'easeOutQuart' },
          interaction: { mode: 'index', axis: 'x', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...TOOLTIP_STYLE,
              callbacks: {
                title: (items) => {
                  const raw = items[0]?.raw as { x: number } | undefined;
                  return `Input: ${(raw?.x ?? 0).toFixed(2)}`;
                },
                label: (ctx) => {
                  const raw = ctx.raw as { y: number };
                  return `${ctx.dataset.label}: ${raw.y.toFixed(3)}`;
                },
              },
            },
          },
          scales: {
            x: {
              type: 'linear',
              min: X_MIN,
              max: X_MAX,
              grid: { color: (c) => zeroLine(c.tick.value) },
              ticks: { stepSize: 1, ...axisTicks },
              title: axisTitle('Input'),
            },
            y: isFn
              ? {
                  suggestedMin: -1.25,
                  suggestedMax: 3,
                  grid: { color: (c) => zeroLine(c.tick.value) },
                  ticks: axisTicks,
                  title: axisTitle('Output'),
                }
              : {
                  min: -0.05,
                  max: 1.1,
                  grid: { color: (c) => zeroLine(c.tick.value) },
                  ticks: axisTicks,
                  title: axisTitle('Slope'),
                },
          },
          layout: { padding: { top: 12, right: 116 } },
        },
        plugins: [crosshairPlugin, endLabelPlugin],
      });
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [view]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="A New Activation Function"
        description="A1 used Tanh as its activation: accurate, but expensive on small networks. A2 switches to leaky ReLU, which costs less CPU. Those savings went into a larger network that is more accurate at the same CPU budget."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">Tanh (A1) vs Leaky ReLU (A2)</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">{SUBTITLES[view]}</div>
        <div className="relative h-[400px] w-full max-[500px]:h-[340px]">
          <canvas ref={canvasRef} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">View</span>
            <div className="flex items-center gap-1 rounded-lg bg-white/5 p-0.5">
              {(
                [
                  { id: 'function', label: 'Function' },
                  { id: 'derivative', label: 'Derivative' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setView(opt.id)}
                  className={`rounded-md px-2.5 py-1 transition ${
                    view === opt.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
